// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// components
import { lowerCase } from 'src/utils/stringFormatter';
import { Programme, ProgrammeActivityType, ProgrammeType } from 'src/resources/api/programme/types.api';
import { IconBuilding, IconChart, IconCoin, IconGlob, IconMapPoint, IconMultiMapPoint, IconSettings, IconSmile, IconSquares } from 'src/libraries/icons';
import { NavListProps } from './types';
import { FeatureTypes } from 'src/@types/feature';
import { getProgrammeName } from 'src/utils/get-programme-name';

// ----------------------------------------------------------------------

const generateRouteChildren = (el: Programme) => {
  const children: { [key: string]: NavListProps[] } = {
    CUSTOMER: [
      { title: 'Overview', path: PATH_DASHBOARD.programs.overview(lowerCase(el.type), el.code) },
      { title: 'Offsets', path: PATH_DASHBOARD.programs.offsets(lowerCase(el.type), el.code) },
      { title: 'Impact', path: PATH_DASHBOARD.programs.impact(lowerCase(el.type), el.code) },
      { title: 'Insights', path: PATH_DASHBOARD.programs.insights(lowerCase(el.type), el.code) },
      { title: 'Landing page', path: PATH_DASHBOARD.programs.landings(lowerCase(el.type), el.code) },
      { title: 'Settings', path: PATH_DASHBOARD.programs.settings(lowerCase(el.type), el.code) },
    ],
    MANAGED: [
      { title: 'Overview', path: PATH_DASHBOARD.programs.overview(lowerCase(el.type), el.code) },
      {
        title: 'Footprints',
        path: PATH_DASHBOARD.programs.footprints(lowerCase(el.type), el.code),
      },
      { title: 'Offsets', path: PATH_DASHBOARD.programs.offsets(lowerCase(el.type), el.code) },
      { title: 'Impact', path: PATH_DASHBOARD.programs.impact(lowerCase(el.type), el.code) },
      { title: 'Insights', path: PATH_DASHBOARD.programs.insights(lowerCase(el.type), el.code) },
      { title: 'Landing page', path: PATH_DASHBOARD.programs.landings(lowerCase(el.type), el.code) },
      { title: 'Settings', path: PATH_DASHBOARD.programs.settings(lowerCase(el.type), el.code) },
    ],
    MYCARBONCLICK: [
      { title: 'Overview', path: PATH_DASHBOARD.programs.overview(lowerCase(el.type), el.code) },
      { title: 'Participants', path: PATH_DASHBOARD.programs.participants(lowerCase(el.type), el.code) },
      { title: 'Offsets', path: PATH_DASHBOARD.programs.offsets(lowerCase(el.type), el.code) },
      { title: 'Impact', path: PATH_DASHBOARD.programs.impact(lowerCase(el.type), el.code) },
      { title: 'Insights', path: PATH_DASHBOARD.programs.insights(lowerCase(el.type), el.code) },
      { title: 'Landing page', path: PATH_DASHBOARD.programs.landings(lowerCase(el.type), el.code) },
      { title: 'Settings', path: PATH_DASHBOARD.programs.settings(lowerCase(el.type), el.code) },
    ],
  }

  let result = children[el?.type];

  if (el?.type === ProgrammeType.MYCARBONCLICK) {
    return result
  }

  if (el?.activityType === ProgrammeActivityType.FLIGHT) {
    return result
  }

  if (el?.activityType === ProgrammeActivityType.JOURNEY) {
    return result
  }

  if (el?.activityType === ProgrammeActivityType.NONE) {
    return result?.filter((item) => item?.title !== 'Footprints' && item?.title !== 'Insights');
  }

  return result?.filter((item) => item?.title !== 'Insights');
}

export interface ProgrammeMenu {
  title: string;
  code: string;
  path: string;
  id: string;
  icon: JSX.Element;
  programme: any;
  hideDatePicker?: boolean;
  programmeMenu: NavListProps[];
}

export const getProgrammeDefaultSubtitle = (programmeType?: ProgrammeType) => {
  if (!programmeType) return '';

  switch(programmeType) {
    case ProgrammeType.MANAGED:
      return 'Offsetting activity is processed within your CarbonClick account. You can manage and complete offsets  either for your own impact or on behalf of your customers.';
    case ProgrammeType.CUSTOMER:
      return 'Offsetting is initiated directly by your customers through a connected widget, checkout, or API. These offsets are created automatically when your customers complete their transactions.';
    case ProgrammeType.MYCARBONCLICK:
      return 'Sustainability education and offsetting are delivered through a hosted MyCarbonClick experience, typically white-labelled for your organisation.';
    default:
      return '';
  }
}

export const getProgramMenu = (programmes: Programme[]) => {
  const menu: ProgrammeMenu[] = programmes?.map((el) => {
    const children = generateRouteChildren(el);
    const name = getProgrammeName(el?.name);

    return {
      title: name === 'Mycarbonclick' ? 'My carbonclick' : name,
      code: el.code,
      path: PATH_DASHBOARD.programs.program(lowerCase(el.type), el.code),
      defaultPath: children[0].path,
      id: `nav-item-programme-${el.code}`,
      icon: <IconSquares />,
      subtitle: el?.subtitle || getProgrammeDefaultSubtitle(el.type),
      programme: el.type,
      programmeMenu: children || [],
      isProgramme: true,
    }
  });

  return menu;
};

//

export enum TopNavigationItems {
  offsets = 'offsets',
  programmes = 'programmes',
  communication = 'communication',
  reports = 'reports',
}


const navConfig = [
  {
    title: 'Programmes',
    name: TopNavigationItems.programmes,
    path: '#',
    children: [
      { title: 'Business customers', featureCode: FeatureTypes.MANAGE_BUSINESS_CUSTOMER, path: PATH_DASHBOARD.businessCustomers, id: 'nav-item-business-customers', icon: <IconBuilding />, subtitle: 'Manage businesses that have participated in your programmes. Profiles may be added manually or created automatically.' },
    ],
  },
  {
    title: 'Offsets',
    name: TopNavigationItems.offsets,
    path: '#',
    children: [
      { title: 'Impact', path: PATH_DASHBOARD.offsets.impact, id: 'nav-item-impact', icon: <IconChart />, subtitle: 'See how your carbon emissions have been offset over time.' },
      { title: 'Transactions', path: PATH_DASHBOARD.offsets.transactions, id: 'nav-item-transactions', icon: <IconCoin />, subtitle: 'View your past carbon offset purchases.' },
      { title: 'Individuals', path: PATH_DASHBOARD.individuals, id: 'nav-item-individuals', icon: <IconSmile />, subtitle: 'View individuals and employees who’ve engaged with your programmes.' },
      // Note: Old and not relevant now. Needs to be migrated
      // { title: 'My footprints', featureCode: FeatureTypes.FOOTPRINT, path: PATH_DASHBOARD.offsets.footprints, id: 'nav-item-my-footprints', icon: <IconCalculator />, subtitle: 'Calculate the carbon footprint of and manage your business emissions – from flights, freight, fuel use and more.' },
      { title: 'Projects', path: PATH_DASHBOARD.projects.root, id: 'nav-item-projects', icon: <IconMapPoint />, subtitle: 'Browse our verified carbon offsetting projects.' },
      { title: 'Portfolios', path: PATH_DASHBOARD.baskets.root, id: 'nav-item-portfolios', icon: <IconMultiMapPoint />, subtitle: 'Curated portfolios enable a balanced approach to offsetting by bundling multiple projects together.' },
    ],
  },
  {
    title: 'Communication',
    name: TopNavigationItems.communication,
    path: '#',
    children: [
      { title: 'Landing pages', featureCode: FeatureTypes.LANDING_PAGE, path: PATH_DASHBOARD.communication.landingPages, id: 'nav-item-landing pages', icon: <IconGlob />, subtitle: 'Manage the landing pages your customers use to engage with your programmes.' },
      { title: 'Toolkit', featureCode: FeatureTypes.INTERACTIVE_TOOLS, path: PATH_DASHBOARD.toolkit.root, id: 'nav-item-toolkit', icon: <IconSettings />, subtitle: 'Browse our toolkit.' },
    ],
  },
  {
    title: 'Settings',
    name: 'settings',
    hideOnDesktop: true,
    path: '#',
    children: [
      { title: 'General', path: PATH_DASHBOARD.settings.business, id: 'nav-item-settings-general', icon: <IconSquares /> },
      { title: 'Theme', path: PATH_DASHBOARD.settings.theme, id: 'nav-item-settings-theme', icon: <IconSquares /> },
      { title: 'Default portfolio', path: PATH_DASHBOARD.settings.defaultBasket, id: 'nav-item-settings-default-basket', icon: <IconSquares /> },
      { title: 'Billing', path: PATH_DASHBOARD.settings.billing, id: 'nav-item-settings-billing', icon: <IconSquares /> },
      { title: 'Receipt emails', path: PATH_DASHBOARD.settings.emails, id: 'nav-item-settings-emails', icon: <IconSquares /> },
    ],
  }
];

export const getNavConfig = (programmes: Programme[] = [], merchantFeatures?: { code: string }[]) => {
  const programmeMenuItems = getProgramMenu(programmes);

  const menuWithProgrammes = navConfig.map((section) => {
    if (section.name === 'programmes') {
      return {
        ...section,
        children: [...section.children, ...programmeMenuItems],
      };
    }
    return section;
  })

  const newMenu = menuWithProgrammes.map((section) => {
    const updatedSection = {
      ...section,
      children: section.children?.map((item: any) => {
        return {
          ...item,
          disabled: !item?.featureCode ? false : !merchantFeatures?.find(feature => feature?.code === item?.featureCode)
        }
      }),
    };
    
    return updatedSection;
  })

  return newMenu;
}

export default navConfig;
